import React, { useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';

import { StatusTag } from 'components/StatusTag';
import { Tooltip } from 'components/Tooltip';

import { SessionEventRow } from '../SessionEventRow';

import {
  EXIT_CAUSE_ICONS,
  formatDuration,
  formatOffset,
  getJourneyEventElementId,
  getJourneyScreenElementId,
  joinJourneyParts,
  type AnalyticsStudentSessionDetailDto,
  type JourneyHighlight,
} from '../../TimelineSection.helpers';
import {
  buildJourneyEventLog,
  type JourneyLogChapter,
} from '../../journeyEventLog';
import { useJourneyTechMode } from '../../journeyTechMode';

import styles from './SessionEventLog.module.scss';

/** Invisible scroll targets for events and screens folded into another row or header. */
const Anchors = ({
  indexes,
  getId,
}: {
  indexes: number[];
  getId: (index: number) => string;
}) => (
  <>
    {indexes.map((index) => (
      <span key={index} id={getId(index)} className={styles.anchor} />
    ))}
  </>
);

const Gap = ({ durationMs }: { durationMs: number }) => (
  <div className={styles.gap}>
    <span />
    <span className={styles.gapLine} aria-hidden />
    <Typography
      className={styles.gapText}
      component="span"
      variant="body3"
      mode="compact"
    >
      {getTranslation('student_page.journey.log.gap', {
        duration: formatDuration(durationMs),
      })}
    </Typography>
  </div>
);

const isChapterHighlighted = (
  chapter: JourneyLogChapter,
  highlight: JourneyHighlight | null,
) =>
  highlight != null &&
  (highlight.target === 'screen'
    ? chapter.screenIndexes.includes(highlight.index)
    : chapter.headerEventIndexes.includes(highlight.index));

/**
 * Event log of one session in chapters (one lesson / trainer / screen each) with
 * sticky headers: task pairs and repeats are collapsed, the exit is one row and
 * non-blocking warnings wait for «Технические детали».
 */
const SessionEventLog = ({
  detail,
  highlight,
  studentEmail,
}: {
  detail: AnalyticsStudentSessionDetailDto;
  highlight: JourneyHighlight | null;
  studentEmail?: string | null;
}) => {
  const { i18n } = useTranslation();
  const techMode = useJourneyTechMode();
  const chapters = useMemo(
    () => buildJourneyEventLog(detail, { techMode, studentEmail }),
    [detail, techMode, studentEmail, i18n.language],
  );

  if (detail.events.length === 0) {
    return (
      <Typography className={styles.empty} variant="body2">
        {getTranslation('student_page.journey.detail.events_empty')}
      </Typography>
    );
  }

  return (
    <div className={styles.log}>
      {chapters.map((chapter) => (
        <section
          key={chapter.screen.index}
          id={getJourneyScreenElementId(chapter.screen.index)}
          className={cn(styles.group, {
            [styles.groupHighlighted]: isChapterHighlighted(chapter, highlight),
          })}
        >
          {chapter.gapBeforeMs != null ? (
            <Gap durationMs={chapter.gapBeforeMs} />
          ) : null}
          <Anchors
            indexes={chapter.screenIndexes.filter(
              (index) => index !== chapter.screen.index,
            )}
            getId={getJourneyScreenElementId}
          />
          <div className={styles.groupHeader}>
            <Typography
              className={styles.groupTitle}
              variant="body2"
              weight="semiBold"
              mode="compact"
              title={chapter.label}
            >
              {chapter.label}
            </Typography>
            <Typography
              className={styles.groupMeta}
              variant="body3"
              mode="compact"
            >
              {joinJourneyParts([
                getTranslation('student_page.journey.log.screen_meta', {
                  offset: formatOffset(chapter.offsetMs),
                  duration: formatDuration(chapter.durationMs),
                }),
                chapter.tasksSummary,
              ])}
            </Typography>
            <span className={styles.groupLine} aria-hidden />
            {chapter.hasError ? (
              <StatusTag variant="error" icon={EXIT_CAUSE_ICONS.error}>
                {getTranslation('student_page.journey.log.screen_error')}
              </StatusTag>
            ) : null}
            {chapter.hasSlow ? (
              <StatusTag variant="warning" icon={EXIT_CAUSE_ICONS.stuck}>
                {getTranslation('student_page.journey.log.screen_slow')}
              </StatusTag>
            ) : null}
          </div>

          <Anchors
            indexes={chapter.headerEventIndexes}
            getId={getJourneyEventElementId}
          />
          {chapter.headerNotes.length > 0 ||
          chapter.headerTechnicalDetails.length > 0 ? (
            <div className={styles.groupNotes}>
              {chapter.headerNotes.length > 0 ? (
                <Typography
                  className={styles.groupNote}
                  variant="body3"
                  mode="compact"
                >
                  {joinJourneyParts(chapter.headerNotes)}
                </Typography>
              ) : null}
              {chapter.headerTechnicalDetails.map((details) => (
                <Typography
                  key={details}
                  className={styles.groupTechnicalDetails}
                  variant="body3"
                  mode="compact"
                >
                  {details}
                </Typography>
              ))}
            </div>
          ) : null}

          {chapter.rows.map((row) => (
            <React.Fragment key={row.event.index}>
              {row.showGapBefore ? <Gap durationMs={row.gapBeforeMs} /> : null}
              <Anchors
                indexes={row.eventIndexes.filter(
                  (index) => index !== row.event.index,
                )}
                getId={getJourneyEventElementId}
              />
              <SessionEventRow
                highlighted={
                  highlight?.target === 'event' &&
                  row.eventIndexes.includes(highlight.index)
                }
                hideCatalogContext={row.hideCatalogContext}
                hideCatalogLines={row.hideCatalogLines}
                appVersion={detail.env?.appVersion}
                studentEmail={studentEmail}
                event={row.event}
                title={row.title}
                titleNote={row.note}
                repeatCount={row.repeatCount}
                foldedEvents={row.foldedEvents}
              />
            </React.Fragment>
          ))}

          <Anchors
            indexes={chapter.hiddenTechnicalEventIndexes}
            getId={getJourneyEventElementId}
          />
          {chapter.hiddenTechnicalEventIndexes.length > 0 ? (
            <div className={styles.hiddenTechnical}>
              <Tooltip
                text={getTranslation(
                  'student_page.journey.log.hidden_technical_hint',
                )}
                triggerClassName={styles.hiddenTechnicalTrigger}
                triggerElement={
                  <Typography
                    className={styles.hiddenTechnicalText}
                    component="span"
                    variant="body3"
                    mode="compact"
                  >
                    {getTranslation(
                      'student_page.journey.log.hidden_technical',
                      { count: chapter.hiddenTechnicalEventIndexes.length },
                    )}
                  </Typography>
                }
              />
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
};

export default React.memo(SessionEventLog) as any;
